import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  School as SchoolIcon,
  People as PeopleIcon,
  SupervisorAccount as AdminIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { db } from '../../firebase/config';
import {
  collection,
  query,
  where,
  getDocs,
  getCountFromServer,
} from 'firebase/firestore';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const Statistics = () => {
  const [stats, setStats] = useState({
    totalEstablishments: 0,
    totalPersonnel: 0,
    totalAdmins: 0,
    personnelByType: [],
    personnelByEstablishment: [],
  });
  const [loading, setLoading] = useState(true);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      // Nombre total d'établissements
      const establishmentsSnapshot = await getCountFromServer(collection(db, 'establishments'));
      const totalEstablishments = establishmentsSnapshot.data().count;

      // Nombre total d'administrateurs
      const adminsQuery = query(collection(db, 'users'), where('role', '==', 'admin'));
      const adminsSnapshot = await getCountFromServer(adminsQuery);
      const totalAdmins = adminsSnapshot.data().count;

      // Nombre total de personnel et répartition par type
      const personnelQuery = query(collection(db, 'users'), 
        where('role', 'in', ['utilisateur']));
      const personnelSnapshot = await getDocs(personnelQuery);
      const totalPersonnel = personnelSnapshot.size;

      // Répartition par type
      const personnelByType = {};
      personnelSnapshot.forEach(doc => {
        const type = doc.data().type || 'Non spécifié';
        personnelByType[type] = (personnelByType[type] || 0) + 1;
      });

      // Répartition par établissement
      const establishmentsSnapshot2 = await getDocs(collection(db, 'establishments'));
      const personnelByEstablishment = [];
      
      for (const establishment of establishmentsSnapshot2.docs) {
        const personnelCount = await getCountFromServer(
          query(
            collection(db, 'users'),
            where('establishmentId', '==', establishment.id)
          )
        );
        personnelByEstablishment.push({
          name: establishment.data().nom,
          value: personnelCount.data().count,
        });
      }

      setStats({
        totalEstablishments,
        totalPersonnel,
        totalAdmins,
        personnelByType: Object.entries(personnelByType).map(([name, value]) => ({
          name,
          value,
        })),
        personnelByEstablishment,
      });
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    }
    setLoading(false);
  };

  return (
    <Grid container spacing={3}>
      {/* Cartes de statistiques */}
      <Grid item xs={12} md={4}>
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            height: 140,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SchoolIcon color="primary" sx={{ mr: 1 }} />
            <Typography component="h2" variant="h6" color="primary">
              Établissements
            </Typography>
          </Box>
          <Typography component="p" variant="h3" sx={{ my: 2 }}>
            {stats.totalEstablishments}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={4}>
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            height: 140,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PeopleIcon color="primary" sx={{ mr: 1 }} />
            <Typography component="h2" variant="h6" color="primary">
              Personnel
            </Typography>
          </Box>
          <Typography component="p" variant="h3" sx={{ my: 2 }}>
            {stats.totalPersonnel}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={4}>
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            height: 140,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AdminIcon color="primary" sx={{ mr: 1 }} />
            <Typography component="h2" variant="h6" color="primary">
              Administrateurs
            </Typography>
          </Box>
          <Typography component="p" variant="h3" sx={{ my: 2 }}>
            {stats.totalAdmins}
          </Typography>
        </Paper>
      </Grid>

      {/* Graphiques */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader
            title="Répartition du personnel par type"
            action={
              <Tooltip title="Rafraîchir">
                <IconButton onClick={fetchStatistics}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <CardContent>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={stats.personnelByType}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {stats.personnelByType.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardHeader
            title="Personnel par établissement"
            action={
              <Tooltip title="Rafraîchir">
                <IconButton onClick={fetchStatistics}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <CardContent>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stats.personnelByEstablishment}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <ChartTooltip />
                  <Bar dataKey="value" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default Statistics;
